import React, {useState} from "react"
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Box,
    Typography,
    Divider,
    Paper,
    IconButton,
    CircularProgress,
    Chip,
} from "@mui/material"
import {
    Close as CloseIcon,
    Add as AddIcon,
    LocalPharmacy as PharmacyIcon,
    Public as CountryIcon,
    Business as ManufacturerIcon,
    Science as ScienceIcon,
    Category as CategoryIcon,
    Inventory as PackageIcon,
} from "@mui/icons-material"
import {useTranslation} from "react-i18next"
import PropTypes from "prop-types"

const AddProductDialog = ({open, onClose, onSubmit, loading = false, categories}) => {
    const {t} = useTranslation()
    const [formData, setFormData] = useState({
        name: "",
        country: "",
        manufacturer: "",
        activeIngredients: "",
        category: "",
        packageType: "",
    })
    const [errors, setErrors] = useState({})


    const packageTypes = ["Tablets", "Capsules", "Inhaler", "Injection", "Syrup", "Cream", "Drops", "Ointment", "Patches"]

    const validateForm = () => {
        const newErrors = {}

        if (!formData.name.trim()) {
            newErrors.name = t("ProductNameMsg")
        }
        if (!formData.country.trim()) {
            newErrors.country = t("CountryMsg")
        }
        if (!formData.manufacturer.trim()) {
            newErrors.manufacturer = t("ManufacturerMsg")
        }
        if (!formData.activeIngredients.trim()) {
            newErrors.activeIngredients = t("ActiveIngredientsMsg")
        }
        if (!formData.category) {
            newErrors.category = t("CategoryMsg")
        }
        if (!formData.packageType) {
            newErrors.packageType = t("PackageTypeMsg")
        }

        setErrors(newErrors)
        return Object.keys(newErrors).length === 0
    }

    const handleSubmit = () => {
        if (validateForm()) {
            onSubmit(formData)
        }
    }

    const handleClose = () => {
        setFormData({
            name: "",
            country: "",
            manufacturer: "",
            activeIngredients: "",
            category: "",
            packageType: "",
        })
        setErrors({})
        onClose()
    }

    const handleInputChange = (field, value) => {
        setFormData((prev) => ({...prev, [field]: value}))
        if (errors[field]) {
            setErrors((prev) => ({...prev, [field]: ""}))
        }
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                },
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    backgroundColor: "primary.main",
                    color: "white",
                    position: "relative",
                    py: 3,
                }}
            >
                <Box sx={{display: "flex", alignItems: "center", gap: 2}}>
                    <PharmacyIcon sx={{fontSize: 28}}/>
                    <Typography variant="h5" component="div" sx={{fontWeight: 600}}>
                        {t("AddNewProduct")}
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{opacity: 0.9, mt: 1}}>
                    {t("AddNewProductDesc")}
                </Typography>
                <IconButton
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: "white",
                        "&:hover": {
                            backgroundColor: "rgba(255,255,255,0.1)",
                        },
                    }}
                >
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{p: 0}}>
                <Box sx={{p: 3}}>
                    {/* Basic Information Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 3,
                            backgroundColor: "#f8f9ff",
                            border: "1px solid #e3e8ff",
                            borderRadius: 2,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 2,
                                color: "#4f46e5",
                                fontWeight: 600,
                            }}
                        >
                            <PharmacyIcon/>
                            {t("BasicInformation")}
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label={t("ProductName")}
                                    value={formData.name}
                                    onChange={(e) => handleInputChange("name", e.target.value)}
                                    error={!!errors.name}
                                    helperText={errors.name}
                                    variant="outlined"
                                    InputProps={{
                                        sx: {backgroundColor: "white"},
                                    }}
                                    placeholder="e.g., Amoxicillin 500mg"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t("ActiveIngredients")}
                                    value={formData.activeIngredients}
                                    onChange={(e) => handleInputChange("activeIngredients", e.target.value)}
                                    error={!!errors.activeIngredients}
                                    helperText={errors.activeIngredients}
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: <ScienceIcon sx={{mr: 1, color: "#6b7280"}}/>,
                                        sx: {backgroundColor: "white"},
                                    }}
                                    placeholder="e.g., Amoxicillin Trihydrate"
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <FormControl fullWidth error={!!errors.category}>
                                    <InputLabel>{t("Category")}</InputLabel>
                                    <Select
                                        value={formData.category}
                                        label={t("Category")}
                                        onChange={(e) => handleInputChange("category", e.target.value)}
                                        sx={{backgroundColor: "white"}}
                                        startAdornment={<CategoryIcon sx={{mr: 1, color: "#6b7280"}}/>}
                                        variant={"outlined"}>
                                        {categories.map((category) => (
                                            <MenuItem key={category} value={category}>
                                                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                                    <Chip label={category} size="small" variant="outlined"
                                                          color="primary"/>
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.category && (
                                        <Typography variant="caption" color="error" sx={{mt: 0.5, ml: 2}}>
                                            {errors.category}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Manufacturing Information Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            mb: 3,
                            backgroundColor: "#f0fdf4",
                            border: "1px solid #bbf7d0",
                            borderRadius: 2,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 2,
                                color: "#059669",
                                fontWeight: 600,
                            }}
                        >
                            <ManufacturerIcon/>
                            {t("ManufacturingDetails")}
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t("Manufacturer")}
                                    value={formData.manufacturer}
                                    onChange={(e) => handleInputChange("manufacturer", e.target.value)}
                                    error={!!errors.manufacturer}
                                    helperText={errors.manufacturer}
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: <ManufacturerIcon sx={{mr: 1, color: "#6b7280"}}/>,
                                        sx: {backgroundColor: "white"},
                                    }}
                                    placeholder="e.g., PharmaCorp Ltd."
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <TextField
                                    fullWidth
                                    label={t("CountryOfOrigin")}
                                    value={formData.country}
                                    onChange={(e) => handleInputChange("country", e.target.value)}
                                    error={!!errors.country}
                                    helperText={errors.country}
                                    variant="outlined"
                                    InputProps={{
                                        startAdornment: <CountryIcon sx={{mr: 1, color: "#6b7280"}}/>,
                                        sx: {backgroundColor: "white"},
                                    }}
                                    placeholder="e.g., United States"
                                />
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Packaging Information Section */}
                    <Paper
                        elevation={0}
                        sx={{
                            p: 3,
                            backgroundColor: "#fef3c7",
                            border: "1px solid #fde68a",
                            borderRadius: 2,
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 2,
                                color: "#d97706",
                                fontWeight: 600,
                            }}
                        >
                            <PackageIcon/>
                            {t("PackagingInformation")}
                        </Typography>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <FormControl fullWidth error={!!errors.packageType}>
                                    <InputLabel>{t("PackageType")}</InputLabel>
                                    <Select
                                        value={formData.packageType}
                                        label={t("PackageType")}
                                        onChange={(e) => handleInputChange("packageType", e.target.value)}
                                        sx={{backgroundColor: "white"}}
                                        startAdornment={<PackageIcon sx={{mr: 1, color: "#6b7280"}}/>}
                                        variant={"outlined"}>
                                        {packageTypes.map((type) => (
                                            <MenuItem key={type} value={type}>
                                                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                                    <PackageIcon sx={{fontSize: 16, color: "#6b7280"}}/>
                                                    {type}
                                                </Box>
                                            </MenuItem>
                                        ))}
                                    </Select>
                                    {errors.packageType && (
                                        <Typography variant="caption" color="error" sx={{mt: 0.5, ml: 2}}>
                                            {errors.packageType}
                                        </Typography>
                                    )}
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Paper>

                    {/* Summary */}
                    {Object.values(formData).some((value) => value.trim()) && (
                        <Paper
                            elevation={0}
                            sx={{
                                p: 3,
                                marginTop: 3,
                                backgroundColor: "#f1f5f9",
                                border: "1px solid #cbd5e1",
                                borderRadius: 2,
                            }}
                        >
                            <Typography variant="h6" sx={{mb: 2, color: "#475569", fontWeight: 600}}>
                                {t("ProductSummary")}
                            </Typography>
                            <Box sx={{display: "flex", flexWrap: "wrap", gap: 1}}>
                                {formData.name &&
                                    <Chip label={`${t("Name")}: ${formData.name}`} variant="outlined" size="small"/>}
                                {formData.category && (
                                    <Chip label={`${t("Category")}: ${formData.category}`} variant="outlined" size="small"
                                          color="primary"/>
                                )}
                                {formData.manufacturer && (
                                    <Chip label={`${t("Manufacturer")}: ${formData.manufacturer}`} variant="outlined"
                                          size="small"/>
                                )}
                                {formData.country &&
                                    <Chip label={`${t("Country")}: ${formData.country}`} variant="outlined" size="small"/>}
                                {formData.packageType && (
                                    <Chip label={`${t("Package")}: ${formData.packageType}`} variant="outlined" size="small"/>
                                )}
                            </Box>
                        </Paper>
                    )}
                </Box>
            </DialogContent>

            <Divider/>

            <DialogActions sx={{p: 3, gap: 2}}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    size="large"
                    sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                    }}
                >
                    {t("Cancel")}
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16}/> : <AddIcon/>}
                    sx={{
                        borderRadius: 2,
                        textTransform: "none",
                        fontWeight: 600,
                    }}
                >
                    {loading ? t("AddingProduct") : t("AddProduct")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

AddProductDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    loading: PropTypes.bool,
}

export default AddProductDialog
